import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowLeft, Calendar, ExternalLink, Send } from "lucide-react";
import api from "@/lib/api";

const DEFAULT_CALENDLY = "https://calendly.com/luminary-advisory/strategy-session";

/**
 * Reusable Calendly booking modal with Back + Close so users are never trapped.
 * Tries the configured Settings.calendly_url first; falls back to a placeholder
 * URL — the placeholder shows a graceful fallback panel instead of a broken iframe.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   calendlyUrl string (optional override; otherwise read from /api/settings)
 *   title      string (optional)
 */
export default function CalendlyModal({ open, onClose, calendlyUrl, title }) {
  const [url, setUrl] = useState(calendlyUrl || "");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (calendlyUrl) {
      setUrl(calendlyUrl);
      return;
    }
    if (!open) return;
    api
      .get("/settings")
      .then((r) => setUrl((r.data?.calendly_url || "").trim()))
      .catch(() => setUrl(""));
  }, [open, calendlyUrl]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const effectiveUrl = url || DEFAULT_CALENDLY;
  const usingFallback = !url;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--navy)]/85 backdrop-blur-sm p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Schedule a Strategy Session"
      data-testid="calendly-modal"
    >
      {/* Backdrop close */}
      <button
        aria-label="Close"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        data-testid="calendly-backdrop"
      />

      <div className="relative bg-white w-full max-w-[1100px] max-h-[92vh] flex flex-col border border-[var(--navy)] shadow-2xl">
        {/* Modal header */}
        <div className="flex items-center justify-between px-5 sm:px-7 py-4 border-b border-[var(--border)] bg-[var(--navy)] text-white">
          <div className="flex items-center gap-3">
            <Calendar size={16} className="text-[var(--gold)]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">
                Strategy Session
              </div>
              <div className="font-display font-bold text-base sm:text-lg leading-tight mt-0.5">
                {title || "Schedule a 60-min Strategy Session"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80 hover:text-[var(--gold)] inline-flex items-center gap-1.5 px-2 py-1"
              data-testid="calendly-back-btn"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center border border-white/30 hover:bg-[var(--gold)] hover:border-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              aria-label="Close"
              data-testid="calendly-close-btn"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <div className="flex-1 overflow-auto bg-[var(--bg-2)]">
          {usingFallback ? (
            <FallbackPanel onClose={onClose} />
          ) : (
            <div className="relative w-full h-full min-h-[640px] bg-white">
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--slate-light)]">
                  Loading Calendly…
                </div>
              )}
              <iframe
                src={effectiveUrl}
                title="Calendly booking"
                className="w-full h-full min-h-[640px] border-0"
                onLoad={() => setLoaded(true)}
                data-testid="calendly-iframe"
              />
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="px-5 sm:px-7 py-4 border-t border-[var(--border)] bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--slate-light)]">
            60-minute scoping conversation · Remote-first practice · We respond within one business day.
          </div>
          <div className="flex items-center gap-3">
            {!usingFallback && (
              <a
                href={effectiveUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--navy)] hover:text-[var(--gold)] inline-flex items-center gap-1.5"
                data-testid="calendly-open-newtab"
              >
                Open in new tab <ExternalLink size={11} />
              </a>
            )}
            <Link
              to="/strategy-session"
              onClick={onClose}
              className="btn-secondary text-[10px] px-4 py-2"
              data-testid="calendly-fallback-form"
            >
              <Send size={12} /> Use form instead
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FallbackPanel({ onClose }) {
  return (
    <div className="p-8 sm:p-12 max-w-[760px] mx-auto" data-testid="calendly-fallback">
      <div className="eyebrow">Booking link pending</div>
      <h3 className="font-display font-black text-3xl sm:text-4xl mt-3 leading-tight text-[var(--navy)]">
        We&apos;re finalising our public booking calendar.
      </h3>
      <p className="mt-5 text-[var(--slate)] leading-relaxed">
        Until our Calendly link is live, our practice desk handles scheduling personally —
        so every session is matched to a relevant practice lead.
      </p>
      <ul className="mt-6 space-y-3 text-[15px] text-[var(--navy)]">
        <li className="border-l-2 border-[var(--gold)] pl-4">
          Use the Strategy Session form — we confirm two slot options within one business day.
        </li>
        <li className="border-l-2 border-[var(--gold)] pl-4">
          Or email <a href="mailto:support@luminaryin.com" className="text-[var(--gold)] hover:underline">support@luminaryin.com</a> directly.
        </li>
      </ul>
      <div className="mt-9 flex flex-wrap gap-3">
        <Link
          to="/strategy-session"
          onClick={onClose}
          className="btn-primary"
          data-testid="calendly-fallback-cta"
        >
          Go to Strategy Session form <Send size={13} />
        </Link>
        <button
          onClick={onClose}
          className="btn-secondary"
          data-testid="calendly-fallback-close"
        >
          <ArrowLeft size={13} /> Back to site
        </button>
      </div>
    </div>
  );
}
